// Embedded tools render full-bleed inside the main panel, with
// "Open standalone ↗" as the escape hatch. Registered SSO tools use the
// same-origin /api/admin/sso/:tool mint endpoint, whose short-lived signed
// token lands both views signed in.

import { Link, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/primitives';

import { useMemo } from 'react';
import { useIsMobile } from '../lib/useIsMobile';
import { documentsReturn, toolConfig, toolEntry, toolPermissions } from '../lib/toolEmbed';
export { TOOLS } from '../lib/toolEmbed';

export default function ToolEmbed() {
  const { tool } = useParams<{ tool: string }>();
  const [params] = useSearchParams();
  const config = toolConfig(tool ?? '');
  const mobile = useIsMobile();
  const requested = params.get('url');
  // Resolve on entry, not on an unrelated shell render: never reload an active
  // editor/recording when the user toggles the OS theme from More.
  const src = useMemo(() => toolEntry(tool ?? '', requested, true,
    typeof document !== 'undefined' && document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'), [tool, requested]);
  const standalone = toolEntry(tool ?? '', requested, false, 'dark');
  const returnTo = documentsReturn(params.get('returnTo'));

  if (!config) {
    // A bad /tools/<x> deep link (Phase 1.11) — a clear not-found with a way
    // back, rather than a silent redirect or a blank embed. (SPA client routes
    // are served with a 200 shell, so a true HTTP 404 isn't applicable here.)
    return (
      <>
        <PageHeader title="Not found" />
        <div className="page-body">
          <div className="content">
            <div className="empty-note">
              There’s no tool called “{tool}”.{' '}
              <Link to="/overview" style={{ color: 'var(--accent)' }}>
                Back to overview →
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="tool-embed">
      <PageHeader title={config.name}>
        <a
          className="btn tool-standalone"
          aria-label="Open standalone"
          title="Open standalone"
          href={standalone}
          target="_blank"
          rel="noreferrer"
        >
          {mobile ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M14 4h6v6M20 4 10 14M10 4H4v16h16v-6" /></svg> : 'Open standalone ↗'}
        </a>
      </PageHeader>
      {returnTo && <Link className="tool-documents-return" to={returnTo}>← Back to Documents</Link>}
      {/* SSO embeds start on a same-origin mint URL that redirects to the
          tool, so the permission must name the tool's origin explicitly:
          a bare allow="clipboard-write" only covers the src attribute's
          origin (this app) and never reaches the redirected document. */}
      <iframe
        className="tool-frame"
        src={src}
        title={config.name}
        allow={toolPermissions(tool ?? '')}
      />
    </div>
  );
}
